import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Shuffle, MapPin, Calendar as CalendarIcon, Users, ShieldCheck, Ticket, Lock, Sparkles, Building2, Clock, Repeat, User as UserIcon } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";
import RichTextEditor from "../../components/RichTextEditor";

const COVER_THEMES = [
  "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1600&q=80",
  "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=1600&q=80",
  "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=1600&q=80",
  "https://images.unsplash.com/photo-1519744792095-2f2205e87b6f?w=1600&q=80",
  "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1600&q=80",
  "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=1600&q=80",
];

const isoLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60000).toISOString().slice(0, 16);
};

const timeToMinutes = (h, m) => h * 60 + m;
const fmt12 = (h, m) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function AdminEventCompose() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [campuses, setCampuses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [eventType, setEventType] = useState("general"); // "general" | "capital"
  const [investors, setInvestors] = useState([]);
  // Slot-based programs a 1:1 session can belong to (Founder Labs, Funding
  // Labs, General Sessions, …) — data-driven from /programs.
  const [programs, setPrograms] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    objective: "",
    cover_image: COVER_THEMES[0],
    start_at: "",
    end_at: "",
    timezone: "America/Los_Angeles",
    location_kind: "campus",
    campus_id: "cmp_pa",
    custom_venue: "",
    virtual_link: "",
    visibility: "public",
    privacy: "public",
    require_approval: false,
    ticket_price: 0,
    capacity: null,
    waitlist_enabled: false,
    group_registration: false,
    registration_open: true,
    hosts: [],
    ticket_types: [],
    custom_questions: [],
  });

  // 1:1 session-only state
  const [cs, setCs] = useState({
    program_id: "",
    cadence: "weekly",
    investor_name: "",
    investor_firm: "",
    investor_bio: "",
    investor_photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    focus_areas_str: "",
    first_date: "",
    start_h: 14, start_m: 0,
    end_h: 16, end_m: 0,
    slot_minutes: 30,
    booking_mode: "approval",
    weeks: 1,
    booking_fields: {
      name: { enabled: true, required: true },
      email: { enabled: true, required: true },
      linkedin: { enabled: true, required: false },
      website: { enabled: true, required: false },
      note: { enabled: true, required: true },
    },
  });

  // Campus ids are per-environment seed data, NOT portable: the initial
  // "cmp_pa" below is a dev seed id that production does not have. A <select>
  // whose value matches no option still renders the first one, so the form
  // looked correctly filled while it posted an id the campuses table never
  // had — every 1:1 session create 500'd on the foreign key. Reconcile the
  // default against what actually loaded; an event being edited keeps its own
  // campus because that id IS in the list.
  useEffect(() => {
    api.get("/campuses").then((r) => {
      const list = r.data || [];
      setCampuses(list);
      setForm((f) => (list.some((c) => c.id === f.campus_id)
        ? f : { ...f, campus_id: list[0]?.id || null }));
    });
  }, []);
  useEffect(() => {
    if (eventType === "capital") {
      api.get("/investors").then((r) => setInvestors(r.data)).catch(() => {});
      api.get("/programs").then((r) => setPrograms((r.data || []).filter((p) => p.delivery === "slot_series"))).catch(() => {});
    }
  }, [eventType]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/events/${id}`).then((r) => {
        const e = r.data;
        setForm({
          ...form, ...e,
          start_at: isoLocal(e.start_at), end_at: isoLocal(e.end_at),
          location_kind: e.virtual_link ? "virtual" : e.custom_venue ? "custom" : "campus",
        });
      }).catch(() => toast.error("Event not found"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const shuffleCover = () => {
    const others = COVER_THEMES.filter((c) => c !== form.cover_image);
    setForm({ ...form, cover_image: others[Math.floor(Math.random() * others.length)] });
  };

  // ---- Capital slot preview ----
  const slotPreview = useMemo(() => {
    if (eventType !== "capital") return [];
    const from = timeToMinutes(cs.start_h, cs.start_m);
    const to = timeToMinutes(cs.end_h, cs.end_m);
    if (to <= from || !cs.slot_minutes) return [];
    const out = [];
    for (let t = from; t + cs.slot_minutes <= to; t += cs.slot_minutes) {
      const h = Math.floor(t / 60);
      const m = t % 60;
      out.push(fmt12(h, m));
    }
    return out;
  }, [eventType, cs.start_h, cs.start_m, cs.end_h, cs.end_m, cs.slot_minutes]);

  const applyInvestor = (inv) => setCs({ ...cs, investor_name: inv.name, investor_firm: inv.firm, investor_bio: inv.bio || "", investor_photo: inv.photo || cs.investor_photo, investor_linkedin: inv.linkedin || "", focus_areas_str: (inv.focus_areas || []).join(", ") });

  const submitGeneral = async () => {
    // Name the field that is actually missing. A datetime-local input reports an
    // EMPTY value until BOTH its date and time parts are filled, so a half-filled
    // "Ends" looks complete on screen while the state is still "" — the old
    // catch-all message sent people looking at the wrong field.
    const missing = [
      !form.title && "a title",
      !form.start_at && "a start date AND time",
      !form.end_at && "an end date AND time",
    ].filter(Boolean);
    if (missing.length) return toast.error(`Still needed: ${missing.join(", ")}`);
    if (new Date(form.end_at) <= new Date(form.start_at))
      return toast.error("The end time must be after the start time");
    setBusy(true);
    try {
      const payload = {
        title: form.title, description: form.description, cover_image: form.cover_image,
        objective: form.objective || null,
        start_at: new Date(form.start_at).toISOString(), end_at: new Date(form.end_at).toISOString(),
        timezone: form.timezone,
        campus_id: form.location_kind === "campus" ? form.campus_id : null,
        custom_venue: form.location_kind === "custom" ? form.custom_venue : null,
        virtual_link: form.location_kind === "virtual" ? form.virtual_link : null,
        visibility: form.visibility, privacy: form.privacy,
        require_approval: form.require_approval,
        ticket_price: parseFloat(form.ticket_price || 0),
        capacity: form.capacity ? parseInt(form.capacity) : null,
        waitlist_enabled: form.waitlist_enabled,
        group_registration: form.group_registration,
        registration_open: true,
        hosts: form.hosts, ticket_types: form.ticket_types,
        custom_questions: form.custom_questions, registration_fields: [],
      };
      const r = isEdit ? await api.patch(`/events/${id}`, payload) : await api.post("/events", payload);
      toast.success(isEdit ? "Saved" : "Event created");
      nav(`/admin/events/${r.data.id}/manage`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const submitCapital = async () => {
    if (!cs.program_id) return toast.error("Pick a program (Founder Labs, General Sessions, …)");
    if (!cs.investor_name) return toast.error("Host / investor name is required");
    if (!cs.first_date) return toast.error("Pick the session date");
    if (slotPreview.length === 0) return toast.error("Time window must be longer than one slot");
    setBusy(true);
    try {
      const count = parseInt(cs.weeks) || 1;
      const payload = {
        // Title is optional — backend auto-fills "{program badge} with
        // {investor}" per occurrence and marks it title_source: auto.
        title: (form.title || "").trim() || undefined,
        investor_name: cs.investor_name, investor_firm: cs.investor_firm || "", investor_bio: cs.investor_bio || "",
        investor_photo: cs.investor_photo,
        investor_linkedin: cs.investor_linkedin || null,
        focus_areas: cs.focus_areas_str.split(",").map((s) => s.trim()).filter(Boolean),
        campus_id: form.campus_id,
        first_date: cs.first_date,
        start_hour: cs.start_h, start_minute: cs.start_m,
        end_hour: cs.end_h, end_minute: cs.end_m,
        slot_duration_minutes: cs.slot_minutes,
        booking_mode: cs.booking_mode,
        booking_fields: cs.booking_fields,
        cancellation_cutoff_hours: 24,
        count, cadence: count > 1 ? cs.cadence : undefined,
      };
      const r = await api.post(`/admin/programs/${cs.program_id}/sessions`, payload);
      const created = r.data.created || [];
      toast.success(`${created.length} session${created.length === 1 ? "" : "s"} created · ${created.reduce((n, o) => n + o.slots, 0)} slots`);
      nav("/admin/events");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <AdminShell testId="admin-event-compose">
        <div className="max-w-4xl">
          <button onClick={() => nav("/admin/events")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-4">
            <ArrowLeft size={14} /> All events
          </button>

          {/* Event type toggle */}
          {!isEdit && (
            <div className="glass-panel p-2 flex gap-1 mb-6" data-testid="event-type-toggle">
              <TypeChip active={eventType === "general"} onClick={() => setEventType("general")} icon={CalendarIcon} title="General event" desc="One-off pitch night, workshop, mixer…" testId="type-general" />
              <TypeChip active={eventType === "capital"} onClick={() => setEventType("capital")} icon={Sparkles} title="1:1 Session" desc="Slot-based session · Founder Labs, Funding Labs…" testId="type-capital" />
            </div>
          )}

          {/* HERO: cover + title */}
          <div className="card-hair overflow-hidden">
            <div className="relative">
              <div className="p-6 pb-0">
                <Overline>Cover image</Overline>
                <div className="mt-2">
                  <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} aspect="cover" testId="compose-cover-upload" />
                </div>
                {form.cover_image && (
                  <button onClick={shuffleCover} type="button" className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="compose-shuffle">
                    <Shuffle size={11} /> Try a random cover
                  </button>
                )}
              </div>
            </div>
            <div className="p-8 pt-6">
              <input className="ghost-input text-5xl font-heading font-medium tracking-tighter" placeholder={eventType === "capital" ? "e.g. One-on-one session with Sree Menon" : "Event Name"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="compose-title" />
              {eventType === "capital" && (
                <div className="text-xs text-muted-foreground mt-2 font-mono">
                  Shown on the public session page and in all emails. Leave blank to auto-fill as
                  “{(programs.find((p) => p.id === cs.program_id)?.badge_label) || "1:1 Session"} with {cs.investor_name || "…"}”.
                </div>
              )}

              {/* Fields depend on type */}
              {eventType === "general" ? (
                <div className="mt-8 grid md:grid-cols-2 gap-5">
                  <Field icon={CalendarIcon} label="Starts">
                    <input type="datetime-local" className={`input-field ${!form.start_at ? "dt-empty" : ""}`}
                           value={form.start_at} onChange={(e) => {
                      // Default the end to start + 2h when it is empty or now
                      // invalid, so the commonest way to trip validation — filling
                      // "Starts" and never noticing "Ends" — simply cannot happen.
                      const start = e.target.value;
                      setForm((f) => {
                        const needsEnd = start && (!f.end_at || new Date(f.end_at) <= new Date(start));
                        if (!needsEnd) return { ...f, start_at: start };
                        const end = new Date(new Date(start).getTime() + 2 * 60 * 60 * 1000);
                        const pad = (n) => String(n).padStart(2, "0");
                        const local = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`
                                    + `T${pad(end.getHours())}:${pad(end.getMinutes())}`;
                        return { ...f, start_at: start, end_at: local };
                      });
                    }} data-testid="compose-start" />
                  </Field>
                  <Field icon={CalendarIcon} label="Ends">
                    <input type="datetime-local" className={`input-field ${!form.end_at ? "dt-empty" : ""}`}
                           value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="compose-end" />
                  </Field>
                  <div className="md:col-span-2 -mt-2" data-testid="compose-when-summary">
                    {form.start_at && form.end_at ? (
                      <div className="text-xs font-mono" style={{ color: "hsl(var(--vd-green))" }}>
                        ✓ {new Date(form.start_at).toLocaleString("en-US", {
                             weekday: "short", month: "short", day: "numeric", year: "numeric",
                             hour: "numeric", minute: "2-digit" })}
                        {" → "}
                        {new Date(form.end_at).toLocaleString("en-US", {
                             hour: "numeric", minute: "2-digit" })}
                        {" "}<span className="chip chip-cream-2">{form.timezone}</span>
                      </div>
                    ) : (
                      <div className="text-xs font-mono" style={{ color: "hsl(var(--vd-coral))" }}>
                        {!form.start_at && !form.end_at
                          ? "No date set yet — the greyed mm/dd/yyyy above is a format hint, not a value."
                          : !form.start_at ? "Start not set — pick a date AND a time."
                          : "End not set — pick a date AND a time."}
                      </div>
                    )}
                  </div>

                  <Field icon={MapPin} label="Location" className="md:col-span-2">
                    <div className="grid md:grid-cols-[auto_1fr] gap-2">
                      <select className="input-field md:w-52" value={form.location_kind} onChange={(e) => setForm({ ...form, location_kind: e.target.value })} data-testid="compose-location-kind">
                        <option value="campus">Campus</option>
                        <option value="custom">Custom venue</option>
                        <option value="virtual">Virtual</option>
                      </select>
                      {form.location_kind === "campus" ? (
                        <select className="input-field" value={form.campus_id} onChange={(e) => setForm({ ...form, campus_id: e.target.value })} data-testid="compose-campus">
                          {campuses.map((c) => <option key={c.id} value={c.id}>{`${c.name} — ${c.address}`}</option>)}
                        </select>
                      ) : form.location_kind === "custom" ? (
                        <input className="input-field" placeholder="Venue name or address" value={form.custom_venue} onChange={(e) => setForm({ ...form, custom_venue: e.target.value })} data-testid="compose-venue" />
                      ) : (
                        <input className="input-field" placeholder="https://meet.google.com/…" value={form.virtual_link} onChange={(e) => setForm({ ...form, virtual_link: e.target.value })} data-testid="compose-virtual" />
                      )}
                    </div>
                  </Field>

                  <Field label="Description" className="md:col-span-2">
                    <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} placeholder="Tell attendees what to expect…" minRows={5} testId="compose-description" />
                  </Field>

                  <Field label="Primary objective" className="md:col-span-2" hint="Internal only — pre-fills the post-event Report so you don't have to type it again later.">
                    <textarea rows={2} className="input-field resize-y" value={form.objective || ""} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="What is this event meant to achieve?" data-testid="compose-objective" />
                  </Field>
                </div>
              ) : (
                <CapitalFields cs={cs} setCs={setCs} campuses={campuses} campusId={form.campus_id} setCampusId={(v) => setForm({ ...form, campus_id: v })} investors={investors} applyInvestor={applyInvestor} slotPreview={slotPreview} form={form} setForm={setForm} programs={programs} />
              )}
            </div>
          </div>

          {/* Options row for general only */}
          {eventType === "general" && (
            <div className="mt-6 card-hair p-6" data-testid="compose-options">
              <Overline>Event options</Overline>
              <div className="mt-5 divide-y divide-border">
                <OptionRow icon={Ticket} title="Ticket Price" description="Free or paid entry.">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">$</span>
                    <input type="number" min={0} step={1} className="input-field w-28" value={form.ticket_price} onChange={(e) => setForm({ ...form, ticket_price: e.target.value })} data-testid="compose-price" />
                  </div>
                </OptionRow>
                <OptionRow icon={ShieldCheck} title="Require Approval" description="Manually approve each registration.">
                  <Toggle checked={form.require_approval} onChange={(v) => setForm({ ...form, require_approval: v })} testId="compose-approval" />
                </OptionRow>
                <OptionRow icon={Users} title="Capacity" description="Set a max or leave unlimited.">
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} placeholder="Unlimited" className="input-field w-32" value={form.capacity || ""} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="compose-capacity" />
                    <span className="text-xs text-muted-foreground">Waitlist</span>
                    <Toggle checked={form.waitlist_enabled} onChange={(v) => setForm({ ...form, waitlist_enabled: v })} testId="compose-waitlist" />
                  </div>
                </OptionRow>
                <OptionRow icon={Users} title="Group Registration" description="Allow guests per registration.">
                  <Toggle checked={form.group_registration} onChange={(v) => setForm({ ...form, group_registration: v })} testId="compose-group" />
                </OptionRow>
                <OptionRow icon={Lock} title="Visibility" description="Who can find and register.">
                  <select className="input-field w-52" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="compose-visibility">
                    <option value="public">Public</option>
                    <option value="members_only">Members only</option>
                    <option value="internal">Internal</option>
                  </select>
                </OptionRow>
                <OptionRow icon={Lock} title="Privacy" description="Public listing or private link only.">
                  <select className="input-field w-52" value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value })} data-testid="compose-privacy">
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </OptionRow>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <button onClick={eventType === "capital" ? submitCapital : submitGeneral} disabled={busy} className="btn-primary" data-testid="compose-submit">
              {busy ? "Saving…" : isEdit ? "Save changes" : eventType === "capital" ? "Create 1:1 Session(s)" : "Create Event"}
            </button>
          </div>
        </div>
    </AdminShell>
  );
}

function CapitalFields({ cs, setCs, campuses, campusId, setCampusId, investors, applyInvestor, slotPreview, form, setForm, programs }) {
  const totalSlots = slotPreview.length * (parseInt(cs.weeks) || 1);
  return (
    <div className="mt-8 space-y-6">
      {/* Program — required: allowances, badges, and reporting hang off it. */}
      <Field icon={Sparkles} label="Program">
        <select className="input-field" value={cs.program_id}
          onChange={(e) => {
            const p = programs.find((x) => x.id === e.target.value);
            setCs({ ...cs, program_id: e.target.value, cadence: (p?.cadence && p.cadence !== "adhoc") ? p.cadence : cs.cadence });
          }} data-testid="cs-program">
          <option value="">Pick a program…</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="text-[11px] text-muted-foreground mt-1.5">
          Which program this session belongs to (member booking allowances apply per program).
          Manage programs under <span className="font-mono">Programs</span>.
        </div>
      </Field>

      {/* Investor block */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <UserIcon size={13} style={{ color: "hsl(var(--vd-green))" }} />
          <Overline>Investor</Overline>
          {investors.length > 0 && (
            <select className="input-field text-xs w-48 ml-auto" onChange={(e) => { const i = investors.find((x) => x.name === e.target.value); if (i) applyInvestor(i); }} defaultValue="" data-testid="investor-picker">
              <option value="">Load from previous…</option>
              {investors.map((inv) => <option key={inv.name} value={inv.name}>{inv.name} · {inv.firm}</option>)}
            </select>
          )}
        </div>
        <div className="grid md:grid-cols-[100px_1fr] gap-4">
          <div className="w-24">
            <ImageUpload value={cs.investor_photo} onChange={(url) => setCs({ ...cs, investor_photo: url })} aspect="square" testId="investor-photo-upload" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="input-field" placeholder="Investor name" value={cs.investor_name} onChange={(e) => setCs({ ...cs, investor_name: e.target.value })} data-testid="investor-name" />
            <input className="input-field" placeholder="Firm" value={cs.investor_firm} onChange={(e) => setCs({ ...cs, investor_firm: e.target.value })} data-testid="investor-firm" />
            <input className="input-field md:col-span-2" placeholder="LinkedIn (linkedin.com/in/…)" value={cs.investor_linkedin} onChange={(e) => setCs({ ...cs, investor_linkedin: e.target.value })} data-testid="investor-linkedin" />
            <div className="md:col-span-2">
              <RichTextEditor value={cs.investor_bio} onChange={(html) => setCs({ ...cs, investor_bio: html })} placeholder="Short bio / description — use bold, links, and lists to give founders context…" minRows={3} testId="investor-bio" />
            </div>
            <input className="input-field md:col-span-2" placeholder="Focus areas (comma-separated) — Dev tools, Applied AI, Consumer…" value={cs.focus_areas_str} onChange={(e) => setCs({ ...cs, focus_areas_str: e.target.value })} data-testid="investor-focus" />
          </div>
        </div>
      </div>

      {/* Session date + campus + time */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field icon={CalendarIcon} label="Session date">
          <input type="date" className="input-field" value={cs.first_date} onChange={(e) => setCs({ ...cs, first_date: e.target.value })} data-testid="cs-date" />
          {cs.first_date && (
            <div className="text-[11px] font-mono text-muted-foreground mt-1">{new Date(cs.first_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          )}
        </Field>
        <Field icon={Building2} label="Campus">
          <select className="input-field" value={campusId} onChange={(e) => setCampusId(e.target.value)} data-testid="cs-campus">
            {campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <Field icon={Clock} label="Start (PT)" className="md:col-span-1">
          <div className="flex gap-1">
            <select className="input-field" value={cs.start_h} onChange={(e) => setCs({ ...cs, start_h: parseInt(e.target.value) })} data-testid="cs-start-h">
              {[...Array(24).keys()].map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
            </select>
            <select className="input-field" value={cs.start_m} onChange={(e) => setCs({ ...cs, start_m: parseInt(e.target.value) })} data-testid="cs-start-m">
              {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
            </select>
          </div>
        </Field>
        <Field label="End (PT)">
          <div className="flex gap-1">
            <select className="input-field" value={cs.end_h} onChange={(e) => setCs({ ...cs, end_h: parseInt(e.target.value) })} data-testid="cs-end-h">
              {[...Array(24).keys()].map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
            </select>
            <select className="input-field" value={cs.end_m} onChange={(e) => setCs({ ...cs, end_m: parseInt(e.target.value) })} data-testid="cs-end-m">
              {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
            </select>
          </div>
        </Field>
        <Field label="Slot length (min)">
          <input type="number" min={5} step={5} className="input-field" value={cs.slot_minutes} onChange={(e) => setCs({ ...cs, slot_minutes: parseInt(e.target.value) || 30 })} data-testid="cs-slot-mins" />
        </Field>
        <Field label="# slots">
          <div className="input-field bg-white/40 text-center font-mono" data-testid="cs-slot-count">{slotPreview.length}</div>
        </Field>
      </div>

      {/* Slot preview */}
      <div className="glass-panel p-4" data-testid="cs-slot-preview">
        <Overline>Live slot preview</Overline>
        {slotPreview.length === 0 ? (
          <div className="text-xs text-muted-foreground mt-3">Adjust the window so it&apos;s at least one slot long.</div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {slotPreview.map((t, i) => (
              <span key={i} className="chip chip-green text-[11px] font-mono" data-testid={`preview-slot-${i}`}>{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* Booking mode + Recurring */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Booking mode">
          <div className="flex gap-1 p-1 bg-white/40 rounded-2xl border border-white/60">
            <ModePill active={cs.booking_mode === "first_come"} onClick={() => setCs({ ...cs, booking_mode: "first_come" })} testId="cs-mode-fcfs">First-come, first-served</ModePill>
            <ModePill active={cs.booking_mode === "approval"} onClick={() => setCs({ ...cs, booking_mode: "approval" })} testId="cs-mode-approval">Approval-based</ModePill>
          </div>
        </Field>
        <Field icon={Repeat} label="Schedule">
          <div className="flex gap-1 p-1 bg-white/40 rounded-2xl border border-white/60">
            <ModePill active={cs.weeks === 1} onClick={() => setCs({ ...cs, weeks: 1 })} testId="cs-schedule-oneoff">One-off session</ModePill>
            <ModePill active={cs.weeks > 1} onClick={() => setCs({ ...cs, weeks: Math.max(2, cs.weeks) })} testId="cs-schedule-recurring">Recurring</ModePill>
          </div>
          {cs.weeks > 1 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="cs-weeks-block">
              <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-widest">Repeat</span>
              <select className="input-field w-32" value={cs.cadence} onChange={(e) => setCs({ ...cs, cadence: e.target.value })} data-testid="cs-cadence">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
              <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-widest">for</span>
              <input type="number" min={2} max={26} className="input-field w-20" value={cs.weeks} onChange={(e) => setCs({ ...cs, weeks: Math.max(2, parseInt(e.target.value) || 2) })} data-testid="cs-weeks" />
              <span className="text-[11px] font-mono text-muted-foreground">sessions</span>
            </div>
          )}
          <div className="text-[11px] font-mono text-muted-foreground mt-2">
            {cs.weeks > 1
              ? `Creates ${cs.weeks} separate session events (${cs.cadence}), ${slotPreview.length} slots each — ${totalSlots} total.`
              : `Creates 1 session event with ${slotPreview.length} slot${slotPreview.length === 1 ? "" : "s"}.`}
          </div>
        </Field>
      </div>

      {/* Booking fields */}
      <div className="glass-panel p-4" data-testid="cs-booking-fields">
        <Overline>Founder submission fields</Overline>
        <p className="text-[11px] text-muted-foreground mt-1">What founders fill in when they book / request a slot.</p>
        <div className="mt-4 space-y-2">
          {Object.entries(cs.booking_fields).map(([k, cfg]) => (
            <div key={k} className="flex items-center justify-between p-2.5 rounded-xl border border-white/60 bg-white/40" data-testid={`cs-field-${k}`}>
              <div>
                <div className="text-sm font-medium capitalize">{k === "note" ? "What are you working on?" : k}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{k}</div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCs({ ...cs, booking_fields: { ...cs.booking_fields, [k]: { ...cfg, enabled: e.target.checked } } })} className="accent-primary" data-testid={`cs-field-${k}-enabled`} />
                  Show
                </label>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <input type="checkbox" checked={cfg.required} disabled={!cfg.enabled} onChange={(e) => setCs({ ...cs, booking_fields: { ...cs.booking_fields, [k]: { ...cfg, required: e.target.checked } } })} className="accent-primary" data-testid={`cs-field-${k}-required`} />
                  Required
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground">
        1:1 sessions default to members-only (from the program). Visibility is fixed to <span className="chip chip-purple text-[10px]">Members-only</span>. Times stored in UTC, shown in America/Los_Angeles.
      </div>
    </div>
  );
}

function ModePill({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} className={`flex-1 px-3 py-2 rounded-xl text-xs transition ${active ? "font-medium" : "text-muted-foreground"}`} style={{ background: active ? "hsl(var(--vd-green))" : "transparent", color: active ? "white" : undefined }} data-testid={testId}>{children}</button>
  );
}

function TypeChip({ active, onClick, icon: Icon, title, desc, testId }) {
  return (
    <button onClick={onClick} className="flex-1 text-left p-4 rounded-xl transition" style={{ background: active ? "hsl(var(--vd-green) / 0.14)" : "transparent", border: active ? "1px solid hsl(var(--vd-green) / 0.35)" : "1px solid transparent" }} data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: active ? "hsl(var(--vd-green))" : "hsl(var(--secondary))", color: active ? "white" : undefined }}>
          <Icon size={14} />
        </div>
        <div className="font-heading">{title}</div>
      </div>
      <div className="text-xs text-muted-foreground mt-1.5">{desc}</div>
    </button>
  );
}

function Field({ icon: Icon, label, children, className = "", hint }) {
  return (
    <label className={className}>
      <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5">{Icon && <Icon size={12} />} {label}</div>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground mt-1 font-mono">{hint}</div>}
    </label>
  );
}

function OptionRow({ icon: Icon, title, description, children }) {
  return (
    <div className="py-4 flex items-center justify-between gap-6">
      <div className="flex items-start gap-3">
        {Icon && <div className="h-9 w-9 rounded-xl flex items-center justify-center border border-border shrink-0" style={{ background: "hsl(var(--secondary))" }}><Icon size={15} /></div>}
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button onClick={() => onChange(!checked)} className="relative h-6 w-11 rounded-full transition" style={{ background: checked ? "hsl(var(--vd-green))" : "hsl(0 0% 88%)" }} data-testid={testId} type="button">
      <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" style={{ left: checked ? "22px" : "2px" }} />
    </button>
  );
}
